import React from "react";
import "./App.css";
import Board from "./Board";
import Side from "./Side";
import { styled } from "@mui/material/styles";
import { Box, Container, Grow, Link, Typography } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import {
  chooseRandomEl,
  findPossibleMoves,
  rollPosition,
  simulateMovesAtTile,
} from "./Functions";
import { AppState } from "./Types";

const Item = styled(Container)(({ theme }) => ({
  padding: theme.spacing(0),
}));

function App() {
  // TODO: Let user select board size

  const [ticks, setTicks] = React.useState(1);
  const [init, setInit] = React.useState(false);

  function resetBoard(size: number) {
    let boardSize = size;
    let pos = rollPosition(boardSize);
    setAppState({
      ...appState,
      boardSize: size,
      knightPosition: pos,
      pastMoves: [pos],
      finished: false,
      countdown: 3,
    });
    if (!init) {
      setInit(true);
    }
  }

  const [appState, setAppState] = React.useState<AppState>({
    boardSize: 8,
    knightPosition: rollPosition(8),
    pastMoves: [rollPosition(8)],
    pastRuns: [[rollPosition(8)]],
    moving: false,
    looping: false,
    heuristic: false,
    heuristicTarget: null,
    knightSpeed: 500,
    finished: false,
    countdown: 3,
    resetFunction: resetBoard
  });

  if (!init) {
    resetBoard(8);
  }

  React.useEffect(() => {
    if (appState.looping && appState.finished && appState.countdown > 0) {
      const timeout = setTimeout(() => {
        setAppState({
          ...appState,
          countdown: appState.countdown - 1,
        });
        return () => clearTimeout(timeout);
      }, 1000);
    } else if (appState.looping && appState.finished) {
      let newPos = rollPosition(appState.boardSize);
      setAppState({
        ...appState,
        finished: false,
        pastRuns: [...appState.pastRuns, appState.pastMoves],
        knightPosition: newPos,
        pastMoves: [newPos],
        countdown: 3,
      });
    }

    if (!appState.finished) {
      const timer = setInterval(() => {
        const possible = findPossibleMoves(appState);
        let movesAtPossible = possible.map((move) => {
          return {
            row: move[0] + appState.knightPosition.row,
            col: move[1] + appState.knightPosition.col,
            moves: simulateMovesAtTile({
              ...appState,
              knightPosition: {
                row: move[0] + appState.knightPosition.row,
                col: move[1] + appState.knightPosition.col,
              },
              pastMoves: [
                ...appState.pastMoves,
                {
                  row: move[0] + appState.knightPosition.row,
                  col: move[1] + appState.knightPosition.col,
                },
              ],
            }),
          };
        });

        let heuristicTarget = {
          row: 0,
          col: 0,
          moves: 0,
        };
        if (movesAtPossible.length > 0) {
          heuristicTarget = movesAtPossible.reduce(function (a, b) {
            return a.moves < b.moves ? a : b;
          });
        }

        if (possible.length > 0) {
          if (appState.moving) {
            let targetCoords;
            if (appState.heuristic && heuristicTarget.moves > 0) {
              targetCoords = {
                row: heuristicTarget.row,
                col: heuristicTarget.col,
              };
            } else {
              let selected = chooseRandomEl(possible);
              targetCoords = {
                row: appState.knightPosition.row + selected[0],
                col: appState.knightPosition.col + selected[1],
              };
            }
            setAppState({
              ...appState,
              pastMoves: [...appState.pastMoves, targetCoords],
              knightPosition: targetCoords,
            });
          }
        } else {
          setAppState({
            ...appState,
            finished: true,
            countdown: 3,
          });
        }
        setTicks(ticks + 1);
      }, appState.knightSpeed);

      return () => clearInterval(timer);
    }
  }, [appState, ticks]);

  return (
    <Container maxWidth={"lg"}>
      <Grid container spacing={0} sx={{ height: "100%", padding: "0 1rem" }} >
        <Grid xs={12} my={1}>
          <Item className="header-in-grid">
            <Grow
              in={true}
              style={{ transformOrigin: "0 0 0" }}
              {...(true ? { timeout: 250 } : {})}
            >
              <Box
                my={1.6}
                sx={{
                  display: "flex",
                  placeItems: "center",
                  placeContent: "center",
                  width: "100%",
                }}
              >
                <div className="logo-container surface">
                  <img
                    src="/projects/knight/logo256.png"
                    className="title-logo"
                    alt="Knight logo"
                  />
                </div>
                <div className="header-text-container surface">
                  <Typography
                    variant="h3"
                    fontWeight={'700'}
                    color={"#e3e3e3"}
                    fontSize={"1.8rem"}
                    className="page-title"
                  >
                    Knight's Tour
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    fontWeight={500}
                    fontSize={"0.81rem"}
                    className="page-subtitle"
                    color={"#b8b8b8"}
                    mt={-0.42}
                  >
                    A playable/tweakable visualizer
                  </Typography>
              </div>
              </Box>
            </Grow>
          </Item>
        </Grid>
        <Grid xs={12} md={7}>
          <Item
            sx={{
              width: "100%",
              height: "100%",
              aspectRatio: "1",
              display: "flex",
              flexDirection: "column",
              placeContent: "center",
            }}
          >
            <Board appState={appState} setAppState={setAppState} />
          </Item>
        </Grid>
        <Grid xs={12} md={5}>
          <Item
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              placeContent: "center",
            }}
          >
            <Side appState={appState} setAppState={setAppState} />
          </Item>
        </Grid>
      </Grid>
    </Container>
  );
}

export default App;
